import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { Field } from "@/components/Field";

const Shell = ({ title, sub, children }) => (
  <div className="max-w-md mx-auto px-4 py-14">
    <div className="card p-6 sm:p-8 fade-up">
      <h1 className="font-display text-2xl font-bold flex items-center gap-2"><KeyRound size={22} /> {title}</h1>
      <p className="mt-1 text-sm" style={{ color: "var(--ink-3)" }}>{sub}</p>
      {children}
    </div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await http.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Forgot password" sub="Enter your account email and we'll send you a link to choose a new password.">
      {sent ? (
        <div data-testid="forgot-sent-message" className="mt-6 rounded-xl p-4 text-sm flex gap-2" style={{ background: "var(--muted-bg)" }}>
          <MailCheck size={18} className="shrink-0" style={{ color: "var(--ok)" }} />
          If an account exists for {email}, a reset link is on its way. It expires in 1 hour. Check your spam folder too.
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field label="Email" testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          {error && <p data-testid="forgot-error" className="text-sm" style={{ color: "var(--alert)" }}>{error}</p>}
          <button data-testid="forgot-submit-button" disabled={busy} className="btn-primary w-full h-11 rounded-full font-semibold flex items-center justify-center gap-2">
            {busy && <Loader2 size={16} className="animate-spin" />} Send reset link
          </button>
        </form>
      )}
      <Link to="/account" data-testid="forgot-back-link" className="mt-4 block text-center text-sm" style={{ color: "var(--accent)" }}>Back to sign in</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setError("Passwords don't match");
    setBusy(true);
    setError("");
    try {
      const { data } = await http.post("/auth/reset-password", { token, password: pw });
      setUser(data);
      toast.success("Password updated — you're signed in");
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Choose a new password" sub="At least 8 characters. This signs you out on other devices.">
      {!token ? (
        <p data-testid="reset-missing-token" className="mt-6 text-sm" style={{ color: "var(--alert)" }}>This link is incomplete. Please request a new reset email.</p>
      ) : (
        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field label="New password" testid="reset-password-input" type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
          <Field label="Confirm new password" testid="reset-password-confirm-input" type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" />
          {error && <p data-testid="reset-error" className="text-sm" style={{ color: "var(--alert)" }}>{error}</p>}
          <button data-testid="reset-submit-button" disabled={busy} className="btn-primary w-full h-11 rounded-full font-semibold flex items-center justify-center gap-2">
            {busy && <Loader2 size={16} className="animate-spin" />} Update password
          </button>
        </form>
      )}
      <Link to="/forgot-password" data-testid="reset-request-new-link" className="mt-4 block text-center text-sm" style={{ color: "var(--accent)" }}>Request a new link</Link>
    </Shell>
  );
}
